import { ATHLETE_SORT_KEYS, type AthleteSortKey, PARTICIPANTS_PAGE_SIZE_DEFAULT, PERSON_GENDERS, type PersonGender } from "@seagames/contracts";
import { z } from "zod";
import { cleanText, idParam, pageParam, sizeParam, textParam } from "../nocs/list-params";

/**
 * Athletes: the filters live in the URL (`/athletes?q&noc&sport&event&gender&sort&page&size`)
 * so Back, a reload and a copied link all show the same page. A bad value is
 * dropped, never an error page. The games is the sidebar's current games.
 */
export const athletesSearch = z.object({
	/** The printed name, any part, any case. */
	q: textParam,
	noc: idParam,
	sport: idParam,
	/** Only meaningful with `sport` — it names one of that sport's events. */
	event: idParam,
	gender: z.enum(PERSON_GENDERS).optional().catch(undefined),
	/** Absent = by name (the API's default). */
	sort: z.enum(ATHLETE_SORT_KEYS).optional().catch(undefined),
	page: pageParam,
	size: sizeParam,
});
export type AthletesSearch = z.infer<typeof athletesSearch>;

/** The FilterBar's draft: empty strings for "any", like a form. */
export interface AthletesDraft extends Record<string, unknown> {
	q: string;
	noc: string;
	sport: string;
	event: string;
	gender: PersonGender | "";
}

export const EMPTY_ATHLETES_DRAFT: AthletesDraft = { q: "", noc: "", sport: "", event: "", gender: "" };

export const athletesDraftFrom = (s: AthletesSearch): AthletesDraft => ({
	q: s.q ?? "",
	noc: s.noc ?? "",
	sport: s.sport ?? "",
	event: s.sport ? (s.event ?? "") : "",
	gender: s.gender ?? "",
});

/**
 * Draft → URL. Empties are dropped, an event only together with its sport,
 * and the page starts over (new filters, new list); the sort and the page
 * size are the table's, not the filter card's, so they stay.
 */
export function athletesSearchFrom(d: AthletesDraft, keep: Pick<AthletesSearch, "sort" | "size"> = {}): AthletesSearch {
	return {
		q: cleanText(d.q),
		noc: d.noc || undefined,
		sport: d.sport || undefined,
		event: d.sport && d.event ? d.event : undefined,
		gender: d.gender || undefined,
		sort: keep.sort,
		size: keep.size,
	};
}

/** How many filters are set (the Reset badge; "a filter is on" for the empty state). */
export const athletesFilterCount = (s: AthletesSearch): number =>
	[s.q, s.noc, s.sport, s.sport ? s.event : undefined, s.gender].filter((v) => v !== undefined).length;

export const athletesSort = (s: AthletesSearch): AthleteSortKey => s.sort ?? "name";

export const athletesPaging = (s: AthletesSearch) => ({ page: s.page ?? 1, pageSize: s.size ?? PARTICIPANTS_PAGE_SIZE_DEFAULT });

/** The `GET /athletes` query for one games — snake_case, as the wire wants it; defaults left out. */
export interface AthletesParams {
	games_id: string;
	search?: string;
	responsible_organization_id?: string;
	sport_id?: string;
	event_id?: string;
	gender?: PersonGender;
	sort?: AthleteSortKey;
	page: number;
	page_size: number;
}

export function athletesParams(gamesId: string, s: AthletesSearch): AthletesParams {
	const { page, pageSize } = athletesPaging(s);
	return {
		games_id: gamesId,
		search: s.q,
		responsible_organization_id: s.noc,
		sport_id: s.sport,
		event_id: s.sport ? s.event : undefined,
		gender: s.gender,
		sort: s.sort === "noc" ? "noc" : undefined,
		page,
		page_size: pageSize,
	};
}
